<script setup>
  import { Select } from "@/components/episto-ui/select";
  import { Button } from "@/components/ui/button";
  import { GitBranch, Trash2 } from "lucide-vue-next";

  const props = defineProps({
    conditions: { type: Array, default: () => [] },
    // [{ id, question_id, operator, type, answers }]
    questionOptions: { type: Array, default: () => [] },
    // (condition) => [{ value, label }]
    getValueOptions: { type: Function, default: () => () => [] },
  });

  const emit = defineEmits(["update:conditions"]);

  const typeOptions = [
    { value: "is", label: "IS" },
    { value: "is_not", label: "IS NOT" },
  ];

  const operatorOptions = [
    { value: "and", label: "AND" },
    { value: "or", label: "OR" },
  ];

  function patch(idx, changes) {
    emit(
      "update:conditions",
      props.conditions.map((c, i) => (i === idx ? { ...c, ...changes } : c)),
    );
  }

  function addCondition() {
    emit("update:conditions", [
      ...props.conditions,
      { id: `cond_${Date.now()}`, question_id: null, operator: "and", type: "is", answers: [] },
    ]);
  }

  function removeCondition(idx) {
    emit(
      "update:conditions",
      props.conditions.filter((_, i) => i !== idx),
    );
  }
</script>

<template>
  <div class="space-y-2">
    <div
      v-for="(cond, i) in conditions"
      :key="cond.id"
      class="flex items-center gap-1.5">
      <!-- Operator connects condition[i-1] → condition[i] -->
      <Select
        v-if="i > 0"
        class="w-[72px] shrink-0"
        :model-value="conditions[i - 1].operator"
        :options="operatorOptions"
        @update:model-value="patch(i - 1, { operator: $event })" />
      <Select
        class="flex-1 min-w-0"
        :model-value="cond.question_id"
        :options="questionOptions"
        placeholder="Question"
        filterable
        @update:model-value="patch(i, { question_id: $event, answers: [] })" />
      <Select
        class="w-[90px] shrink-0"
        :model-value="cond.type"
        :options="typeOptions"
        @update:model-value="patch(i, { type: $event })" />
      <Select
        class="flex-1 min-w-0"
        :model-value="cond.answers"
        :options="getValueOptions(cond)"
        multiple
        filterable
        placeholder="Values"
        :disabled="!cond.question_id"
        @update:model-value="patch(i, { answers: $event })" />
      <Button
        variant="ghost"
        size="icon-sm"
        class="shrink-0"
        @click="removeCondition(i)">
        <Trash2 class="h-4 w-4" />
      </Button>
    </div>

    <p
      v-if="conditions.length === 0"
      class="text-xs text-muted-foreground">
      No conditions — group includes all respondents
    </p>

    <Button
      class="w-fit gap-2"
      variant="outline"
      size="sm"
      @click="addCondition">
      <GitBranch class="h-4 w-4" />
      Add condition
    </Button>
  </div>
</template>
